/* Shared Stamped widget-API client for the conversion-booster sections
   (review highlights + full reviews).

   Endpoints verified live against this store (see repo history):
   - GET https://stamped.io/api/widget/reviews?storeUrl=&productId=&take=&page=
       -> { total, rating, data: [reviews] }
     Extra params: sortReviews (most-votes|newest|...), rating=N — filters
     the list AND `total` reflects the filtered count.
   - Works with the store's public identifier only; no private keys.

   Guarantees to callers:
   - getSummary() resolves ONCE per product per session (sessionStorage +
     in-flight promise sharing between sections on the same page).
   - Every method resolves to data or null. Never throws, never rejects. */

window.BSStampedClient = window.BSStampedClient || (function () {

  const API = 'https://stamped.io/api/widget/reviews';
  const TTL = 15 * 60 * 1000;
  const pending = {};   // in-flight summary promises, keyed by productId
  const listCache = {}; // page-lifetime review-list cache

  function fetchJson(storeUrl, productId, params) {
    const qs = new URLSearchParams(Object.assign({
      storeUrl: storeUrl,
      productId: productId
    }, params || {}));
    return fetch(API + '?' + qs.toString()).then(function (r) {
      if (!r.ok) throw new Error('stamped http ' + r.status);
      return r.json();
    });
  }

  function normalize(r) {
    return {
      id: r.id,
      author: r.author || '',
      title: r.reviewTitle || '',
      message: r.reviewMessage || '',
      rating: r.reviewRating || 0,
      votes: r.reviewVotesUp || 0,
      verified: (r.reviewVerifiedType || 0) >= 2,
      date: r.reviewDate || '',
      variant: r.productVariantName || ''
    };
  }

  /* Summary: total, average, per-star counts, and the top-voted pool.
     6 requests, made once per product per session. */
  function getSummary(storeUrl, productId) {
    const key = 'bsstamped:' + productId;

    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.t < TTL) {
        return Promise.resolve(cached.summary);
      }
    } catch (e) { /* unreadable cache — refetch */ }

    if (pending[productId]) return pending[productId];

    const main = fetchJson(storeUrl, productId, { take: 30, page: 1, sortReviews: 'most-votes' });
    const perStar = [5, 4, 3, 2, 1].map(function (n) {
      return fetchJson(storeUrl, productId, { take: 1, page: 1, rating: n });
    });

    pending[productId] = Promise.all([main].concat(perStar))
      .then(function (results) {
        const m = results[0];
        const summary = {
          total: m.total || 0,
          rating: m.rating || 0,
          stars: {},
          topVoted: (m.data || []).map(normalize)
        };
        [5, 4, 3, 2, 1].forEach(function (n, i) {
          summary.stars[n] = results[i + 1].total || 0;
        });
        try {
          sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), summary: summary }));
        } catch (e) { /* storage full — fine */ }
        delete pending[productId];
        return summary;
      })
      .catch(function () {
        delete pending[productId];
        return null;
      });

    return pending[productId];
  }

  /* Review lists with sort/rating/pagination. Cached for the page's life. */
  function getReviews(storeUrl, productId, opts) {
    opts = opts || {};
    const params = {
      take: opts.take || 10,
      page: opts.page || 1
    };
    if (opts.sort) params.sortReviews = opts.sort;         // 'most-votes' | 'newest'
    if (opts.rating) params.rating = opts.rating;

    const key = productId + ':' + JSON.stringify(params);
    if (listCache[key]) return Promise.resolve(listCache[key]);

    return fetchJson(storeUrl, productId, params)
      .then(function (r) {
        const out = {
          total: r.total || 0,
          reviews: (r.data || []).map(normalize)
        };
        listCache[key] = out;
        return out;
      })
      .catch(function () {
        return null;
      });
  }

  return {
    getSummary: getSummary,
    getReviews: getReviews
  };

})();
